import axios from 'axios';
import { MNSproCloudApi } from '..';
import {
  GraphApiEndpoint,
  createGraphApi,
} from '../../microsoft-graph/factory';
import {
  Assignment,
  BasicSoftwareResponse,
  PaginatedOfBasicSoftwareResponse,
  SoftwareInquiryEmailEntry,
} from '../api/v2';

export class SoftwareService {
  static async getSoftware(authToken: string, organizationId: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const response = await api.getAllBasicSoftware(
      organizationId,
      undefined,
      undefined,
      undefined,
      undefined,
      20,
    );
    let nextLink: string | null | undefined = response.data.nextLink;
    let software: BasicSoftwareResponse[] = response.data.value;

    while (nextLink) {
      const pageResponse = await axios.get<PaginatedOfBasicSoftwareResponse>(
        nextLink,
        {
          headers: {
            authorization: 'Bearer ' + authToken,
          },
        },
      );
      software = software.concat(pageResponse.data.value);
      nextLink = pageResponse.data.nextLink;
    }

    return software;
  }

  static async sendSoftwareRequest(
    authToken: string,
    organizationId: string,
    softwareRequests: SoftwareInquiryEmailEntry[],
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const software = await api.sendSoftwareInquiry(
      organizationId,
      softwareRequests,
    );

    return software.data;
  }

  static async syncApps(authToken: string, organizationId: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    await api.syncSoftwareStores(organizationId);
  }
  static async getIosStoreLink(authToken: string, organizationId: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const software = await api.getIosStoreLink(organizationId);

    return software.data.link;
  }

  static async getWindowsStoreLink(authToken: string, organizationId: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const software = await api.getWindowsStoreLink(organizationId);

    return software.data.link;
  }

  static async getAndroidStoreLink(
    authToken: string,
    organizationId: string,
    hostname: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const software = await api.getAndroidStoreLink(hostname, organizationId);

    return software.data.link;
  }

  static async getSoftwareById(
    authToken: string,
    softwareId: string,
    organizationId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const software = await api.getSoftware(softwareId, organizationId);
    return software.data;
  }

  static async getAssignableSoftware(
    authToken: string,
    organizationId: string,
    groupIds: string[],
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);
    const software = await api.getAssignedSoftwareOfMdmGroups(
      organizationId,
      groupIds,
    );
    return software.data;
  }

  static async getSoftwareIcon(
    authToken: string,
    softwareId: string,
  ): Promise<string> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.Beta,
    ).get<{ largeIcon: { type: string; value: string } }>(
      `/deviceAppManagement/mobileApps/${softwareId}?$select=largeIcon`,
    );

    return response.data.largeIcon.value;
  }

  static async assignSoftware(
    authToken: string,
    organizationId: string,
    assignments: Assignment[],
    softwareId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.MdmApiFactory(config);

    const software = await api.assignSoftware(
      organizationId,
      assignments,
      softwareId,
    );
    return software.data;
  }
}
